import api from "@/services/api";
import {
  cacheOnboardingStatus,
  currentOnboardingStatusGeneration,
  isOnboardingStatusFresh,
  readOnboardingStatusEntry,
} from "@/services/onboardingStatusCache";

export type OnboardingStep =
  | "signup"
  | "domain"
  | "billing"
  | "buyer"
  | "questions"
  | "simulation"
  | "complete";

export interface OnboardingStatus {
  step: OnboardingStep;
  resume_step: OnboardingStep;
  resume_key: OnboardingStep;
  onboarding_deferred?: boolean;
  can_defer?: boolean;
  domain: string | null;
  report_id: number | null;
  report_created_ts?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  buyer_id?: number | null;
  simulation_id?: number | null;
  billing_completed_at: string | null;
  has_paid_access: boolean;
  onboarding_report_completed?: boolean;
  buyer_completed_at?: string | null;
  questions_completed_at?: string | null;
  simulation_completed_at?: string | null;
  client_reference_id?: string;
  billing_offer_code?: string | null;
}

export interface OnboardingUpdatePayload {
  step?: OnboardingStep;
  domain?: string | null;
  report_id?: number | null;
  buyer_id?: number | null;
  simulation_id?: number | null;
  defer_onboarding?: boolean;
}

export const canAccessProduct = (status: OnboardingStatus): boolean =>
  status.has_paid_access || status.resume_key === "complete";

export const shouldResumeOnboarding = (
  status: OnboardingStatus,
  pathname: string,
): boolean =>
  status.resume_key !== "complete" &&
  (!canAccessProduct(status) || pathname === "/");

/**
 * One request in flight at a time, per cache generation.
 *
 * Deduping matters because the shell gate and a page can ask on the same tick
 * (a cold load lands on `/`, which resolves into a company route immediately);
 * without it they fire two identical requests and race to fill the cache.
 *
 * Deduping *across an invalidation* would be a bug, though. A request issued
 * before a 402 carries pre-`activate_deferred_onboarding()` state, so its
 * write-through is discarded — and handing that same promise to the caller who
 * asked *after* the invalidation would leave the cache stale with no request
 * outstanding to correct it. So a generation bump supersedes the in-flight
 * request rather than joining it.
 */
let inFlight: Promise<OnboardingStatus> | null = null;
let inFlightGeneration = -1;

const fetchOnboardingStatus = (): Promise<OnboardingStatus> => {
  const startedAtGeneration = currentOnboardingStatusGeneration();

  if (inFlight && inFlightGeneration === startedAtGeneration) return inFlight;

  const request = api
    .get<OnboardingStatus>("/onboarding/status/")
    .then((response) => {
      cacheOnboardingStatus(response.data, startedAtGeneration);
      return response.data;
    })
    .finally(() => {
      // Only clear if we are still the current request — a superseded one must
      // not null out the newer request that replaced it.
      if (inFlight === request) {
        inFlight = null;
        inFlightGeneration = -1;
      }
    });

  inFlight = request;
  inFlightGeneration = startedAtGeneration;
  return request;
};

/**
 * Always hits the network (deduped), and writes through to the shared cache.
 *
 * Kept as the default export-shaped call because its callers — the onboarding
 * page's 10s poll and the billing panel — genuinely want current data. Their
 * write-through is what keeps the shell gate's copy warm.
 */
export const getOnboardingStatus = (): Promise<OnboardingStatus> => fetchOnboardingStatus();

/**
 * Cache-first read for the app-shell gate: resolves without a request while the
 * cached status is fresh. See `onboardingStatusCache` for why that is safe.
 */
export const loadOnboardingStatus = (): Promise<OnboardingStatus> => {
  const cached = readOnboardingStatusEntry();
  if (isOnboardingStatusFresh(cached) && cached) {
    return Promise.resolve(cached.status);
  }
  return fetchOnboardingStatus();
};

export const updateOnboardingStatus = async (
  payload: OnboardingUpdatePayload,
): Promise<OnboardingStatus> => {
  const response = await api.patch<OnboardingStatus>("/onboarding/status/", payload);
  // The PATCH response is the same fully-serialized status as the GET, so the
  // cache can be advanced from it with no follow-up request.
  cacheOnboardingStatus(response.data);
  return response.data;
};
